import { useEffect, useRef, useState } from 'react'
import Select, {
  type MultiValue,
  type SingleValue,
  type GroupBase,
  type SelectInstance,
} from 'react-select'
import { useTranslation } from 'react-i18next'

type IOption = {
  value: string
  label: string
}

type IProListerSelectBaseProps = {
  options: IOption[]
  placeholder?: string
  disabledPlaceholder?: string
  defaultValueId?: string
  defaultMultiIds?: string[]
  errorStyling?: boolean
  isOpen?: boolean
  onClose?: () => void
}

type IProListerSelectSingleProps = IProListerSelectBaseProps & {
  isMulti: false
  onChange: (o: SingleValue<IOption>) => void
}

type IProListerSelectMultiProps = IProListerSelectBaseProps & {
  isMulti: true
  onChange: (o: MultiValue<IOption>) => void
}

type IProListerSelectProps =
  | IProListerSelectSingleProps
  | IProListerSelectMultiProps

export const ProListerSelect = ({
  options,
  onChange,
  isMulti,
  placeholder = 'Välj',
  disabledPlaceholder = '',
  defaultValueId = '',
  defaultMultiIds = [],
  errorStyling,
  isOpen: isOpenProp,
  onClose,
}: IProListerSelectProps) => {
  const { t } = useTranslation()

  const ref = useRef<SelectInstance<IOption, boolean, GroupBase<IOption>>>(null)
  const [selected, setIsSelected] = useState<
    SingleValue<IOption> | MultiValue<IOption>
  >(null)
  const isDisabled = !options || options.length === 0
  const [isOpen, setIsOpen] = useState(isOpenProp)
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(
    null
  )

  useEffect(() => {
    // Set the portal target to document.body for better positioning control
    setMenuPortalTarget(document.body)
  }, [])

  useEffect(() => {
    if (isOpenProp !== undefined) {
      setIsOpen(isOpenProp)
    }
  }, [isOpenProp])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const selectElement = ref.current?.controlRef
      if (selectElement && !selectElement.contains(event.target as Node)) {
        setIsOpen(false)
        onClose?.()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  const customStyles = {
    option: (provided) => ({
      ...provided,
    }),
    control: (styles, state) => {
      return {
        ...styles,
        display: 'flex',
        backgroundColor: 'white',
        width: state.isMulti ? 370 : 200,
        opacity: !state.isDisabled ? 1 : 0.3,
        borderRadius: 0,
        border: errorStyling
          ? '1px solid #da3530 !important'
          : 'solid 1px #b0b0b0',
      }
    },
    singleValue: (styles, state) => {
      const transition = 'opacity 300ms'

      return {
        ...styles,
        transition,
        width: 400,
        overflowX: 'none',
        background: 'white',
      }
    },
    menu: (styles) => ({
      ...styles,
      width: 400,
      backgroundColor: 'white',
      position: menuPortalTarget ? 'absolute' : 'absolute',
      top: menuPortalTarget ? undefined : 32,
      left: menuPortalTarget ? undefined : 0,
      zIndex: '1000',
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: '1000',
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      color: 'white',
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      backgroundColor: 'black',
      color: 'white',
    }),
  }

  const getDefault = () => {
    if (!isMulti) {
      return defaultValueId
        ? options.find((o) => o.value === defaultValueId)
        : null
    } else {
      const defaultValues = defaultMultiIds
        ? options.filter((o) => defaultMultiIds.includes(o.value))
        : null
      return defaultValues
    }
  }

  useEffect(() => {
    if (isMulti && defaultMultiIds.length > 0) {
      const defaultValues = defaultMultiIds
        ? options.filter((o) => defaultMultiIds.includes(o.value))
        : []
      setIsSelected(defaultValues as any)
    }
  }, [defaultMultiIds.length])

  return (
    <Select
      key={`my_unique_select_key__${defaultValueId}${JSON.stringify(options)}`}
      options={
        isMulti && selected && (selected as any).length === 5 ? [] : options
      }
      styles={{
        ...customStyles,
      }}
      isDisabled={isDisabled}
      onChange={(o) => {
        if (isMulti && Array.isArray(o) && o.length <= 5) {
          setIsSelected(o)
          onChange(o as MultiValue<IOption>)
        } else if (!isMulti && !Array.isArray(o)) {
          onChange(o as SingleValue<IOption>)
        }
      }}
      defaultValue={getDefault()}
      placeholder={
        isDisabled && disabledPlaceholder ? disabledPlaceholder : placeholder
      }
      noOptionsMessage={() => {
        return selected && (selected as any).length === 5
          ? t('select_optionMessage')
          : t('select_optionNoResults')
      }}
      isMulti={isMulti}
      menuIsOpen={isOpen}
      onMenuOpen={() => {
        setIsOpen(true)
      }}
      onMenuClose={() => {
        setIsOpen(false)
      }}
      ref={ref}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      menuPlacement="auto"
    />
  )
}
